import {Box, Card, CardContent, Typography} from "@mui/material";
import {Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,} from "recharts";
import {ChartTooltip, COLORS} from "../../../ui/Theme";
import {ListDivider} from "../../../ui/ListStyles";
import useResponsive from "../../../components/hooks/useResponsive";
import NotFound from "../../../components/notfound/NotFound";
import {useGlobalMarketSpreadPriceContext} from "../../../context/global-market/GlobalMarketSpreadPriceContext";

const CustomTooltip = ({active, payload}) => {
    if (active && payload && payload.length) {
        const {x, y, m} = payload[0].payload;
        return (
            <ChartTooltip>
                <Typography sx={{fontSize: "12px", fontWeight: 900}}>{x}</Typography>
                <ListDivider flexItem/>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                    }}
                >
                    <Box sx={{display: "flex"}}>
                        <Typography sx={{fontSize: "12px", color: COLORS.BLUE}}>نسبت قیمت:</Typography>
                        <Typography sx={{fontSize: "12px", color: COLORS.BLUE, fontWeight: 900}}>
                            {y}
                        </Typography>
                    </Box>
                    <Box sx={{display: "flex"}}>
                        <Typography sx={{fontSize: "12px", color: COLORS.ORANGE}}>میانگین:</Typography>
                        <Typography sx={{fontSize: "12px", color: COLORS.ORANGE, fontWeight: 900}}>
                            {m}
                        </Typography>
                    </Box>
                </Box>
            </ChartTooltip>
        );
    }
    return null;
};

const GlobalMarketSpreadPriceChart = () => {
    const {chartData} = useGlobalMarketSpreadPriceContext();
    const {isMd} = useResponsive();
    const formattedData = chartData.map((item) => ({
        x: item.trade_date,
        y: item.avg_price,
        m: item.mean,
    }));

    const totalDataLength = formattedData?.length || 0;
    const startIndex = Math.max(0, totalDataLength - (isMd ? 100 : 50));
    const endIndex = totalDataLength - 1;

    return (
        <Card
            sx={{
                border: "1px solid",
                borderColor: COLORS.SOFTGRAY,
                padding: 0,
                minHeight: 345,
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    alignItems: "center",
                }}
            >
                <Typography
                    sx={{fontSize: {xs: "16px", md: "20px"}, fontWeight: 700}}
                >
                    نسبت قیمت
                </Typography>
                {chartData.length < 1 ? (
                    <Box sx={{height: 385}}>
                        <NotFound/>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={385}>
                        <LineChart
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            data={formattedData}
                            margin={{top: 0, right: 0, bottom: 20, left: 0}}
                        >
                            <CartesianGrid strokeDasharray="0 0" stroke={COLORS.SOFTGRAY}/>
                            <XAxis
                                stroke={COLORS.GRAY}
                                dataKey="x"
                                angle={-25}
                                textAnchor="middle"
                                tickMargin={10}
                                tickFormatter={(date) => {
                                    const [year, month] = date.split("-");
                                    return `${year}-${month}`;
                                }}
                            />
                            <YAxis
                                textAnchor="start"
                                tickMargin={10}
                                stroke={COLORS.GRAY}
                                domain={[Math.min(...formattedData.map((d) => d.y)), "auto"]}
                            />
                            <Legend
                                wrapperStyle={{
                                    width: "fit-content",
                                    top: -10,
                                    right: 0,
                                    lineHeight: "20px",
                                }}
                                verticalAlign="top"
                                formatter={(value) => {
                                    if (value === "y") {
                                        return (
                                            <span style={{marginRight: "5px"}}>نسبت قیمت</span>
                                        );
                                    } else if (value === "m") {
                                        return (
                                            <span style={{marginRight: "5px"}}>میانگین</span>
                                        );
                                    }
                                    return value;
                                }}
                            />

                            <Tooltip cursor={{ fill: COLORS.SOFTGRAY }} content={<CustomTooltip/>}/>
                            <Line
                                type="monotone"
                                dataKey="y"
                                stroke={COLORS.BLUE}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="m"
                                stroke={COLORS.ORANGE}
                                strokeWidth={2}
                                dot={false}
                            />

                            <Brush
                                dataKey="x"
                                height={30}
                                startIndex={startIndex}
                                endIndex={endIndex}
                                stroke={COLORS.BLUE}
                                travellerWidth={10}
                                y={354}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default GlobalMarketSpreadPriceChart;
